import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth, useLogout } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Flame, Trash2, LogOut, Search, UserPlus, X, Shield, MapPin, Filter, Check, RefreshCw, Zap, Camera, ImageIcon, List, Star, ArrowLeft, CalendarDays, Loader2, Upload, Briefcase, ThumbsUp, ThumbsDown } from "lucide-react";
import type { Venue } from "@shared/schema";
import { CreateVenueDialog } from "@/components/CreateVenueDialog";

interface AdminUser {
  id: number;
  username: string;
  role: string;
}

interface Assignment {
  id: number;
  userId: number;
  venueId: number;
}

export default function AdminPanel() {
  const { user, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const logout = useLogout();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [assignUserId, setAssignUserId] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activePanel, setActivePanel] = useState<"venues" | "google" | "ra-events" | "applications">("venues");

  // RA Events ingestion state
  const [raJsonText, setRaJsonText] = useState<string>("");
  const [raInlineError, setRaInlineError] = useState<string | null>(null);
  const [raSubmitting, setRaSubmitting] = useState<boolean>(false);
  const [raResult, setRaResult] = useState<{ inserted: number; skipped: number; errors: number; at: Date } | null>(null);
  const [raServerError, setRaServerError] = useState<string | null>(null);
  const [raClearing, setRaClearing] = useState<boolean>(false);
  const [raClearResult, setRaClearResult] = useState<{ deleted: number; at: Date } | null>(null);

  function handleRaFileSelected(file: File | null) {
    if (!file) return;
    setRaInlineError(null);
    setRaServerError(null);
    setRaResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? "");
      setRaJsonText(text);
    };
    reader.onerror = () => setRaInlineError("Could not read the selected file");
    reader.readAsText(file);
  }

  async function handleRaIngestSubmit() {
    setRaInlineError(null);
    setRaServerError(null);
    setRaResult(null);

    const trimmed = raJsonText.trim();
    if (!trimmed) {
      setRaInlineError("Please upload or paste a JSON file first");
      return;
    }
    let parsed: any;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      setRaInlineError("Invalid JSON — please check the file and try again");
      return;
    }
    if (!Array.isArray(parsed)) {
      setRaInlineError("Expected a JSON array of events");
      return;
    }

    setRaSubmitting(true);
    try {
      const res = await fetch("/api/admin/events/ingest-ra", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: parsed }),
      });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        setRaServerError(json?.message || `Request failed (${res.status})`);
        return;
      }
      setRaResult({
        inserted: Number(json.inserted ?? 0),
        skipped: Number(json.skipped ?? 0),
        errors: Number(json.errors ?? 0),
        at: new Date(),
      });
    } catch (err: any) {
      setRaServerError(err?.message || "Network error");
    } finally {
      setRaSubmitting(false);
    }
  }

  async function handleRaClear() {
    const ok = window.confirm("This will delete all Resident Advisor events. Are you sure?");
    if (!ok) return;

    setRaInlineError(null);
    setRaServerError(null);
    setRaResult(null);
    setRaClearResult(null);
    setRaClearing(true);
    try {
      const res = await fetch("/api/admin/events/clear-ra", {
        method: "DELETE",
        credentials: "include",
      });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        setRaServerError(json?.message || `Request failed (${res.status})`);
        return;
      }
      setRaClearResult({ deleted: Number(json.deleted ?? 0), at: new Date() });
    } catch (err: any) {
      setRaServerError(err?.message || "Network error");
    } finally {
      setRaClearing(false);
    }
  }

  function formatRaTimestamp(d: Date): string {
    const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${date} · ${time}`;
  }

  const { data: venues } = useQuery<Pick<Venue, 'id' | 'name' | 'type' | 'capacityLevel' | 'isSponsored'>[]>({ queryKey: ["/api/admin/venues/summary"], staleTime: 0, refetchOnMount: "always" });
  const { data: businessUsers } = useQuery<AdminUser[]>({ queryKey: ["/api/admin/business-users"] });

  const { data: googleStatus, refetch: refetchGoogleStatus } = useQuery<{
    totalVenues: number;
    matchedVenues: number;
    venuesWithPhotos: number;
    totalPhotos: number;
    matchJobStatus: {
      running: boolean;
      total: number;
      processed: number;
      matched: number;
      noMatch: number;
      errors: number;
      lastRun: string | null;
      currentVenue: string;
    };
    updateDataJobStatus: {
      running: boolean;
      total: number;
      processed: number;
      updated: number;
      errors: number;
      lastRun: string | null;
      currentVenue: string;
    };
    serpHistogramJobStatus: {
      lastRun: string | null;
      nextRun: string | null;
      lastResult: string;
      lastSummary: string | null;
      lastLogs: string[] | null;
      processed: number;
      withHistogram: number;
      withAddress: number;
      errors: number;
      excludedCooldown: number;
    };
    claudeEstimateJobStatus: {
      lastRun: string | null;
      nextRun: string | null;
      lastResult: string;
      lastSummary: string | null;
      lastLogs: string[] | null;
      enabled: boolean;
    };
  }>({
    queryKey: ["/api/admin/google/status"],
    enabled: activePanel === "google",
    refetchInterval: activePanel === "google" ? 10000 : false,
  });

  const [matchJobRunning, setMatchJobRunning] = useState(false);
  const [matchProgress, setMatchProgress] = useState<{
    total: number; processed: number; matched: number; noMatch: number; errors: number; running: boolean; currentVenue: string;
  } | null>(null);

  const [busynessJobRunning, setBusynessJobRunning] = useState(false);
  const [busynessProgress, setBusynessProgress] = useState<{
    total: number; processed: number; withBusyness: number; errors: number; rateLimited: boolean; running: boolean; currentVenue: string;
  } | null>(null);

  const [popularTimesJobRunning, setPopularTimesJobRunning] = useState(false);
  const [popularTimesProgress, setPopularTimesProgress] = useState<{
    total: number; processed: number; withHistogram: number; errors: number; rateLimited: boolean; running: boolean; currentVenue: string;
  } | null>(null);

  const [updateDataJobRunning, setUpdateDataJobRunning] = useState(false);
  const [updateDataProgress, setUpdateDataProgress] = useState<{
    total: number; processed: number; updated: number; errors: number; running: boolean; currentVenue: string;
  } | null>(null);

  const [photoFillRunning, setPhotoFillRunning] = useState(false);
  const [photoFillProgress, setPhotoFillProgress] = useState<{
    batches: number; processed: number; withPhotos: number; errors: number; remaining: number | null; rateLimited: boolean; done: boolean;
  } | null>(null);
  const [photoFillRetryIn, setPhotoFillRetryIn] = useState<number | null>(null);
  const [photoFillStopping, setPhotoFillStopping] = useState(false);
  const photoFillStopRef = useRef(false);

  const PHOTO_FILL_BACKOFF_BASE = 15;
  const PHOTO_FILL_BACKOFF_MAX = 300;

  // Cancellable countdown. Returns true if it completed, false if the admin
  // pressed Stop while waiting. Polls the stop ref so Stop is responsive.
  async function photoFillCountdown(seconds: number): Promise<boolean> {
    let remainingMs = seconds * 1000;
    setPhotoFillRetryIn(Math.ceil(remainingMs / 1000));
    while (remainingMs > 0) {
      if (photoFillStopRef.current) { setPhotoFillRetryIn(null); return false; }
      const step = Math.min(250, remainingMs);
      await new Promise((r) => setTimeout(r, step));
      remainingMs -= step;
      setPhotoFillRetryIn(Math.ceil(remainingMs / 1000));
    }
    setPhotoFillRetryIn(null);
    return !photoFillStopRef.current;
  }

  function stopPhotoFill() {
    photoFillStopRef.current = true;
    setPhotoFillStopping(true);
  }

  async function runPhotoFill() {
    if (photoFillRunning) return;
    photoFillStopRef.current = false;
    setPhotoFillStopping(false);
    setPhotoFillRunning(true);
    setPhotoFillRetryIn(null);
    const cum = { batches: 0, processed: 0, withPhotos: 0, errors: 0 };
    setPhotoFillProgress({ ...cum, remaining: null, rateLimited: false, done: false });
    let prevRemaining = Infinity;
    let backoff = PHOTO_FILL_BACKOFF_BASE;
    try {
      while (true) {
        if (photoFillStopRef.current) {
          setPhotoFillProgress((p) => (p ? { ...p, done: true } : { ...cum, remaining: null, rateLimited: false, done: true }));
          break;
        }
        const res = await fetch("/api/admin/google/photo-fill", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({} as any));
          toast({ title: "Photo fill failed", description: j?.message || `Request failed (${res.status})`, variant: "destructive" });
          setPhotoFillProgress({ ...cum, remaining: prevRemaining === Infinity ? null : prevRemaining, rateLimited: false, done: true });
          break;
        }
        const data = await res.json();
        cum.batches += 1;
        cum.processed += Number(data.processed ?? 0);
        cum.withPhotos += Number(data.withPhotos ?? 0);
        cum.errors += Number(data.errors ?? 0);
        const remaining = Number(data.remaining ?? 0);
        setPhotoFillProgress({ ...cum, remaining, rateLimited: !!data.rateLimited, done: false });
        refetchGoogleStatus();

        if (remaining === 0) {
          toast({ title: "Photo fill complete", description: `${cum.withPhotos} venues got photos across ${cum.batches} batches.` });
          setPhotoFillProgress({ ...cum, remaining, rateLimited: false, done: true });
          break;
        }

        if (data.rateLimited) {
          // Auto-resume: pause with exponential backoff (capped) and retry on
          // its own, so the long Production run doesn't need babysitting.
          toast({ title: "Paused — SerpAPI rate limit", description: `${remaining} venues still need photos. Retrying in ${backoff}s…` });
          const completed = await photoFillCountdown(backoff);
          if (!completed) {
            setPhotoFillProgress({ ...cum, remaining, rateLimited: true, done: true });
            break;
          }
          backoff = Math.min(backoff * 2, PHOTO_FILL_BACKOFF_MAX);
          continue;
        }

        // Successful batch: reset backoff for the next rate-limit pause.
        backoff = PHOTO_FILL_BACKOFF_BASE;

        if (remaining >= prevRemaining) {
          toast({ title: "Photo fill finished", description: `${remaining} venues have no photos available.` });
          setPhotoFillProgress({ ...cum, remaining, rateLimited: false, done: true });
          break;
        }
        prevRemaining = remaining;
      }
    } catch (err: any) {
      toast({ title: "Photo fill error", description: err?.message || "Network error", variant: "destructive" });
      setPhotoFillProgress((p) => (p ? { ...p, done: true } : { ...cum, remaining: null, rateLimited: false, done: true }));
    } finally {
      setPhotoFillRunning(false);
      setPhotoFillRetryIn(null);
      setPhotoFillStopping(false);
      photoFillStopRef.current = false;
    }
  }

  const [lookupName, setLookupName] = useState("");
  const [lookupLat, setLookupLat] = useState("");
  const [lookupLng, setLookupLng] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);

  const [discoverJobRunning, setDiscoverJobRunning] = useState(false);
  type DiscoverProgress = {
    total: number; searched: number; discovered: number; duplicates: number; errors: number; running: boolean; currentArea: string; lastRun: string | null;
    interrupted?: boolean;
    completedSearches?: Array<{ lat: number; lng: number; searchType: string }>;
    jobMode?: "full" | "targeted";
    targetAreas?: string[];
    recentlyAdded?: string[];
  };
  const [discoverProgress, setDiscoverProgress] = useState<DiscoverProgress | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem("buzzn_discovery_status");
      return raw ? (JSON.parse(raw) as DiscoverProgress) : null;
    } catch { return null; }
  });
  const [targetedAreasInput, setTargetedAreasInput] = useState("");
  const [discoverLog, setDiscoverLog] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("buzzn_discovery_log");
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });
  const discoverLogRef = useRef<HTMLDivElement | null>(null);
  const discoverProgressHydrated = useRef(false);

  useEffect(() => {
    if (!matchJobRunning) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/admin/google/match/progress", { credentials: "include" });
        const data = await res.json();
        setMatchProgress(data);
        if (!data.running) {
          setMatchJobRunning(false);
          refetchGoogleStatus();
          toast({ title: `Matching complete: ${data.matched} matched, ${data.noMatch} not found, ${data.errors} errors` });
        }
      } catch {
        setMatchJobRunning(false);
        setMatchProgress(null);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [matchJobRunning]);

  useEffect(() => {
    if (!busynessJobRunning) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/admin/venues/fetch-busyness/progress", { credentials: "include" });
        const data = await res.json();
        setBusynessProgress(data);
        if (!data.running) {
          setBusynessJobRunning(false);
          const msg = data.rateLimited
            ? `Rate-limited after ${data.processed} venues — ${data.withBusyness} got busyness data`
            : `Busyness fetched: ${data.withBusyness}/${data.processed} venues returned data, ${data.errors} errors`;
          toast({ title: msg, variant: data.rateLimited ? "destructive" : "default" });
        }
      } catch {
        setBusynessJobRunning(false);
        setBusynessProgress(null);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [busynessJobRunning]);

  useEffect(() => {
    if (!popularTimesJobRunning) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/admin/venues/fetch-popular-times/progress", { credentials: "include" });
        const data = await res.json();
        setPopularTimesProgress(data);
        if (!data.running) {
          setPopularTimesJobRunning(false);
          const msg = data.rateLimited
            ? `Rate-limited after ${data.processed} venues — ${data.withHistogram} got histogram data`
            : `Popular times fetched: ${data.withHistogram}/${data.processed} venues returned histogram, ${data.errors} errors`;
          toast({ title: msg, variant: data.rateLimited ? "destructive" : "default" });
        }
      } catch {
        setPopularTimesJobRunning(false);
        setPopularTimesProgress(null);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [popularTimesJobRunning]);

  useEffect(() => {
    if (!updateDataJobRunning) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/admin/google/update-data/progress", { credentials: "include" });
        const data = await res.json();
        setUpdateDataProgress(data);
        if (!data.running) {
          setUpdateDataJobRunning(false);
          refetchGoogleStatus();
          queryClient.invalidateQueries({ queryKey: ["/api/admin/venues/summary"] });
          toast({ title: `Ratings & prices updated for ${data.updated} venues${data.errors > 0 ? `, ${data.errors} errors` : ''}` });
        }
      } catch {
        setUpdateDataJobRunning(false);
        setUpdateDataProgress(null);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [updateDataJobRunning]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/admin/google/discover/progress", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as DiscoverProgress;
        if (cancelled) return;
        setDiscoverProgress(data);
        if (data.running) setDiscoverJobRunning(true);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!discoverJobRunning) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/admin/google/discover/progress", { credentials: "include" });
        const data = await res.json();
        setDiscoverProgress(data);
        if (!data.running) {
          setDiscoverJobRunning(false);
          refetchGoogleStatus();
          queryClient.invalidateQueries({ queryKey: ["/api/admin/venues/summary"] });
          toast({ title: `Discovery complete: ${data.discovered} new venues found, ${data.duplicates} duplicates skipped` });
        }
      } catch {
        setDiscoverJobRunning(false);
        setDiscoverProgress(null);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [discoverJobRunning]);

  useEffect(() => {
    if (!discoverProgressHydrated.current) {
      discoverProgressHydrated.current = true;
      return;
    }
    if (!discoverProgress) return;
    try {
      window.localStorage.setItem("buzzn_discovery_status", JSON.stringify(discoverProgress));
    } catch {}
    const ts = new Date().toLocaleTimeString();
    const area = discoverProgress.currentArea || "—";
    const line = `[${ts}] ${area} — searched ${discoverProgress.searched}/${discoverProgress.total}, discovered ${discoverProgress.discovered}, duplicates ${discoverProgress.duplicates}, errors ${discoverProgress.errors}${!discoverProgress.running ? " — done" : ""}`;
    setDiscoverLog((prev) => {
      if (prev.length > 0 && prev[prev.length - 1] === line) return prev;
      const next = [...prev, line];
      try { window.localStorage.setItem("buzzn_discovery_log", JSON.stringify(next)); } catch {}
      return next;
    });
  }, [discoverProgress]);

  useEffect(() => {
    if (discoverLogRef.current) {
      discoverLogRef.current.scrollTop = discoverLogRef.current.scrollHeight;
    }
  }, [discoverLog]);

  const clearDiscoveryLog = () => {
    setDiscoverLog([]);
    setDiscoverProgress(null);
    try {
      window.localStorage.removeItem("buzzn_discovery_log");
      window.localStorage.removeItem("buzzn_discovery_status");
    } catch {}
  };

  const discoverVenues = useMutation({
    mutationFn: async (opts?: { resume?: boolean }) => {
      const res = await fetch("/api/admin/google/discover", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resume: opts?.resume === true }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed");
      }
      return res.json() as Promise<{ message: string; resume: boolean }>;
    },
    onSuccess: (data) => {
      const resumed = data.resume === true;
      setDiscoverJobRunning(true);
      if (!resumed) {
        setDiscoverProgress(null);
        setDiscoverLog([]);
        try {
          window.localStorage.removeItem("buzzn_discovery_log");
          window.localStorage.removeItem("buzzn_discovery_status");
        } catch {}
      }
      const ts = new Date().toLocaleTimeString();
      const prog = discoverProgress;
      const startLine = resumed && prog
        ? `[${ts}] Resuming from checkpoint — ${prog.searched}/${prog.total} searches already complete, ${prog.discovered} venues found so far.`
        : `[${ts}] Discovery job started`;
      setDiscoverLog((prev) => {
        const next = resumed ? [...prev, startLine] : [startLine];
        try { window.localStorage.setItem("buzzn_discovery_log", JSON.stringify(next)); } catch {}
        return next;
      });
      toast({ title: resumed ? "Resuming London venue discovery..." : "Discovering London venues via Google Places..." });
    },
    onError: () => {
      toast({ title: "Failed to start discovery", variant: "destructive" });
    },
  });

  const discoverVenuesTargeted = useMutation({
    mutationFn: async (opts: { areas: string[]; resume?: boolean }) => {
      const res = await fetch("/api/admin/google/discover/targeted", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ areas: opts.areas, resume: opts.resume === true }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).message || "Failed");
      }
      return res.json() as Promise<{ message: string; areas: string[]; resume: boolean }>;
    },
    onSuccess: (data) => {
      const resumed = data.resume === true;
      setDiscoverJobRunning(true);
      if (!resumed) {
        setDiscoverProgress(null);
        setDiscoverLog([]);
        try {
          window.localStorage.removeItem("buzzn_discovery_log");
          window.localStorage.removeItem("buzzn_discovery_status");
        } catch {}
      }
      const ts = new Date().toLocaleTimeString();
      const prog = discoverProgress;
      const startLine = resumed && prog
        ? `[${ts}] Resuming targeted sweep from checkpoint — ${prog.searched}/${prog.total} searches already complete, ${prog.discovered} venues found so far.`
        : `[${ts}] Targeted sweep started — areas: ${data.areas.join(", ")}`;
      setDiscoverLog((prev) => {
        const next = resumed ? [...prev, startLine] : [startLine];
        try { window.localStorage.setItem("buzzn_discovery_log", JSON.stringify(next)); } catch {}
        return next;
      });
      toast({
        title: resumed
          ? `Resuming targeted sweep across ${data.areas.length} area(s)`
          : `Targeted sweep started across ${data.areas.length} area(s)`,
      });
    },
    onError: (err: Error) => {
      toast({ title: err.message || "Failed to start targeted sweep", variant: "destructive" });
    },
  });

  const parsedTargetedAreas = targetedAreasInput
    .split(/[,\n]+/)
    .map(a => a.trim())
    .filter(a => a.length > 0);

  const matchVenues = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/google/match", { method: "POST", credentials: "include" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed");
      }
      return res.json();
    },
    onSuccess: () => {
      setMatchJobRunning(true);
      setMatchProgress(null);
      toast({ title: "Matching venues to Google Places..." });
    },
    onError: () => {
      toast({ title: "Failed to start matching", variant: "destructive" });
    },
  });

  const fetchBusyness = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/venues/fetch-busyness", { method: "POST", credentials: "include" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed");
      }
      return res.json();
    },
    onSuccess: () => {
      setBusynessJobRunning(true);
      setBusynessProgress(null);
      toast({ title: "Fetching live busyness from Google..." });
    },
    onError: () => {
      toast({ title: "Failed to start busyness fetch", variant: "destructive" });
    },
  });

  const fetchPopularTimes = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/venues/fetch-popular-times", { method: "POST", credentials: "include" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed");
      }
      return res.json();
    },
    onSuccess: () => {
      setPopularTimesJobRunning(true);
      setPopularTimesProgress(null);
      toast({ title: "Fetching popular times histogram from Google..." });
    },
    onError: () => {
      toast({ title: "Failed to start popular times fetch", variant: "destructive" });
    },
  });

  const updateGoogleData = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/google/update-data", { method: "POST", credentials: "include" });
      if (res.status === 409) {
        return { alreadyRunning: true };
      }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed");
      }
      return res.json();
    },
    onSuccess: (data) => {
      setUpdateDataJobRunning(true);
      if (!data?.alreadyRunning) {
        setUpdateDataProgress(null);
      }
      toast({ title: data?.alreadyRunning ? "Update already in progress..." : "Updating ratings & prices..." });
    },
    onError: () => {
      toast({ title: "Failed to start update", variant: "destructive" });
    },
  });

  const toggleSponsor = useMutation({
    mutationFn: async ({ id, isSponsored }: { id: number; isSponsored: boolean }) => {
      await apiRequest("PUT", `/api/admin/venues/${id}/sponsor`, { isSponsored });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/venues/summary"] });
      toast({ title: "Sponsorship updated" });
    },
  });

  const updateVenueType = useMutation({
    mutationFn: async ({ id, type }: { id: number; type: string }) => {
      await apiRequest("PATCH", `/api/admin/venues/${id}/type`, { type });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/venues/summary"] });
      toast({ title: "Venue type updated" });
    },
    onError: () => {
      toast({ title: "Failed to update venue type", variant: "destructive" });
    },
  });

  const deleteVenue = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/venues/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/venues/summary"] });
      setSelectedVenue(null);
      toast({ title: "Venue deleted" });
    },
  });

  const assignVenue = useMutation({
    mutationFn: async ({ userId, venueId }: { userId: number; venueId: number }) => {
      await apiRequest("POST", "/api/admin/assignments", { userId, venueId });
    },
    onSuccess: () => {
      if (selectedVenue) {
        queryClient.invalidateQueries({ queryKey: [`/api/admin/venues/${selectedVenue.id}/assignments`] });
      }
      setAssignUserId("");
      toast({ title: "Business user assigned" });
    },
  });

  const removeAssignment = useMutation({
    mutationFn: async ({ userId, venueId }: { userId: number; venueId: number }) => {
      await apiRequest("DELETE", "/api/admin/assignments", { userId, venueId });
    },
    onSuccess: () => {
      if (selectedVenue) {
        queryClient.invalidateQueries({ queryKey: [`/api/admin/venues/${selectedVenue.id}/assignments`] });
      }
      toast({ title: "Assignment removed" });
    },
  });

  const { data: venueAssignments } = useQuery<Assignment[]>({
    queryKey: [`/api/admin/venues/${selectedVenue?.id}/assignments`],
    enabled: !!selectedVenue,
  });

  if (authLoading) return <div className="min-h-screen bg-background flex items-center justify-center text-white">Loading...</div>;
  if (!user || user.role !== "admin") {
    setLocation("/auth");
    return null;
  }

  const filteredVenues = venues?.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "All" || v.type === typeFilter;
    return matchesSearch && matchesType;
  }) || [];

  return (
    <div className="min-h-screen bg-background text-white">
      <header className="border-b border-zinc-800 px-4 md:px-6 pt-[max(1rem,env(safe-area-inset-top))] pb-3 md:pb-4 flex items-center justify-between gap-2 md:gap-4">
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <Shield className="w-5 h-5 md:w-6 md:h-6 text-blue-500" />
          <h1 className="text-lg md:text-xl font-display font-bold">Admin</h1>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            data-testid="button-view-map"
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-400 hover:text-white"
            onClick={() => setLocation("/")}
          >
            <MapPin className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">View Map</span>
          </Button>
          <Button
            data-testid="button-logout"
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-400 hover:text-white"
            onClick={() => logout.mutateAsync().then(() => setLocation("/auth"))}
          >
            <LogOut className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Logout</span>
          </Button>
        </div>
      </header>

      <div className="border-b border-zinc-800 overflow-x-auto scrollbar-hide">
        <div className="flex px-4 md:px-6 gap-1 min-w-max">
          <button
            data-testid="button-venue-management"
            onClick={() => { setActivePanel("venues"); setSelectedVenue(null); setShowFilterMenu(false); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activePanel === "venues"
                ? 'border-blue-500 text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <List className="w-4 h-4" />
            Venue Management
          </button>
          <button
            data-testid="button-google-panel"
            onClick={() => { setActivePanel("google"); setSelectedVenue(null); setShowFilterMenu(false); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activePanel === "google"
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Camera className="w-4 h-4" />
            Google Photos
          </button>
          <button
            data-testid="button-ra-events-panel"
            onClick={() => { setActivePanel("ra-events"); setSelectedVenue(null); setShowFilterMenu(false); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activePanel === "ra-events"
                ? 'border-primary text-primary'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            RA Events
          </button>
          <button
            data-testid="button-applications-panel"
            onClick={() => { setActivePanel("applications"); setSelectedVenue(null); setShowFilterMenu(false); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activePanel === "applications"
                ? 'border-amber-500 text-amber-400'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Briefcase className="w-4 h-4" />
            Applications
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-105px)]">
        {activePanel === "venues" && <div className={`w-full md:w-96 border-r border-zinc-800 flex flex-col ${selectedVenue ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-zinc-800 space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  data-testid="input-search-venues"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search venues..."
                  className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              <CreateVenueDialog />
              <div className="relative">
                <Button
                  data-testid="button-filter"
                  variant="outline"
                  className="border-zinc-700 text-zinc-400 hover:text-white"
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                  {typeFilter !== "All" && (
                    <Badge className="ml-2 bg-blue-600 text-white text-[10px] px-1.5 py-0">1</Badge>
                  )}
                </Button>
                {showFilterMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 py-1">
                    <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Type</p>
                    {["All", "bar", "pub", "club", "hotel"].map(type => (
                      <button
                        key={type}
                        data-testid={`filter-type-${type}`}
                        onClick={() => { setTypeFilter(type); setShowFilterMenu(false); }}
                        className="w-full px-3 py-2 flex items-center justify-between text-sm hover:bg-zinc-800 transition-colors"
                      >
                        <span className={typeFilter === type ? "font-medium text-white" : "text-zinc-400 capitalize"}>{type === "All" ? "All Types" : type}</span>
                        {typeFilter === type && <Check className="w-4 h-4 text-blue-500" />}
                      </button>
                    ))}
                    {typeFilter !== "All" && (
                      <>
                        <div className="border-t border-zinc-800 my-1" />
                        <button
                          data-testid="button-clear-filters"
                          onClick={() => { setTypeFilter("All"); setShowFilterMenu(false); }}
                          className="w-full px-3 py-2 text-sm text-red-400 hover:bg-zinc-800 transition-colors text-left"
                        >
                          Clear Filters
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            {typeFilter !== "All" && (
              <div className="flex items-center gap-2">
                <Badge
                  className="bg-zinc-800 text-zinc-300 border-zinc-700 text-xs cursor-pointer"
                  onClick={() => setTypeFilter("All")}
                >
                  {typeFilter} <X className="w-3 h-3 ml-1" />
                </Badge>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredVenues.map(venue => (
              <div
                key={venue.id}
                data-testid={`venue-list-item-${venue.id}`}
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/venues/${venue.id}`, { credentials: "include" });
                    if (res.ok) setSelectedVenue(await res.json());
                  } catch {}
                }}
                className={`p-4 border-b border-zinc-800/50 cursor-pointer hover:bg-zinc-900 transition-colors ${selectedVenue?.id === venue.id ? 'bg-zinc-900' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{venue.name}</span>
                      {venue.isSponsored && (
                        <Badge className="bg-red-600/20 text-red-400 border-red-600/30 text-[10px] px-1.5 py-0">
                          <Flame className="w-3 h-3 mr-1" />Sponsored
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-zinc-500 capitalize">{venue.type}</span>
                  </div>
                  <span className="text-sm font-mono text-zinc-400">{venue.capacityLevel}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>}

        <div className={`flex-1 p-4 md:p-6 overflow-y-auto ${activePanel === "venues" && !selectedVenue ? 'hidden md:block' : ''}`}>
          {selectedVenue && activePanel === "venues" ? (
            <div className="max-w-2xl space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Button
                    data-testid="button-back-to-list"
                    variant="outline"
                    size="icon"
                    className="border-zinc-700 md:hidden shrink-0"
                    onClick={() => setSelectedVenue(null)}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <h2 className="text-xl md:text-2xl font-display font-bold">{selectedVenue.name}</h2>
                    <p className="text-zinc-500 text-sm capitalize">{selectedVenue.type} &middot; {selectedVenue.capacityLevel}% capacity</p>
                  </div>
                </div>
                <Button
                  data-testid="button-close-venue"
                  variant="outline"
                  size="icon"
                  className="border-zinc-700 hidden md:flex"
                  onClick={() => setSelectedVenue(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <Card className="bg-zinc-900 border-zinc-800 p-5">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Sponsorship</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Buzzn Sponsored Status</p>
                    <p className="text-xs text-zinc-500">Sponsored venues show a flame marker on the map</p>
                  </div>
                  <Button
                    data-testid="button-toggle-sponsor"
                    variant={selectedVenue.isSponsored ? "default" : "outline"}
                    className={selectedVenue.isSponsored ? "bg-red-600" : "border-zinc-700"}
                    onClick={() => {
                      toggleSponsor.mutate({ id: selectedVenue.id, isSponsored: !selectedVenue.isSponsored });
                      setSelectedVenue({ ...selectedVenue, isSponsored: !selectedVenue.isSponsored });
                    }}
                    disabled={toggleSponsor.isPending}
                  >
                    <Flame className="w-4 h-4 mr-2" />
                    {selectedVenue.isSponsored ? "Remove Sponsorship" : "Make Sponsored"}
                  </Button>
                </div>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="section-venue-discovery">
                <div className="space-y-3">
                  <div>
                    <h2 className="text-2xl font-display font-bold">Venue Discovery</h2>
                    <p className="text-zinc-400 text-sm mt-2" data-testid="text-discovery-warning">
                      ⚠️ This triggers a live Google Places API call across all London grid points and will incur API costs. Only run when new venue coverage is needed.
                    </p>
                  </div>
                  {discoverProgress?.interrupted && !discoverProgress?.running && !discoverJobRunning && (
                    <div
                      className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
                      data-testid="banner-discovery-interrupted"
                    >
                      <p className="text-sm text-amber-200" data-testid="text-discovery-interrupted">
                        ⚠️ Previous job was interrupted at {discoverProgress.searched}/{discoverProgress.total} searches ({discoverProgress.discovered} venues found). Resume from checkpoint or start fresh.
                      </p>
                      {(() => {
                        const completed = discoverProgress.completedSearches ?? [];
                        const uniqueGridPoints = new Set(
                          completed.map((s) => `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`)
                        ).size;
                        const remainingSearches = Math.max(
                          0,
                          (discoverProgress.total ?? 0) - (discoverProgress.searched ?? 0)
                        );
                        const recent = (discoverProgress.recentlyAdded ?? []).slice(-5).reverse();
                        return (
                          <div className="space-y-1.5 text-xs text-amber-100/90">
                            <p data-testid="text-discovery-coverage">
                              Already covered {uniqueGridPoints} grid point{uniqueGridPoints === 1 ? "" : "s"} · {remainingSearches} search{remainingSearches === 1 ? "" : "es"} remaining
                              {discoverProgress.jobMode === "targeted" && discoverProgress.targetAreas && discoverProgress.targetAreas.length > 0
                                ? ` · areas: ${discoverProgress.targetAreas.join(", ")}`
                                : ""}
                            </p>
                            {recent.length > 0 ? (
                              <div data-testid="list-discovery-recent">
                                <span className="text-amber-100/70">Recently added:</span>{" "}
                                <span className="text-amber-50">{recent.join(", ")}</span>
                              </div>
                            ) : (
                              <p className="text-amber-100/60" data-testid="text-discovery-recent-empty">
                                No new venues added before the interruption.
                              </p>
                            )}
                          </div>
                        );
                      })()}
                      <div className="flex gap-2">
                        <Button
                          data-testid="button-resume-discovery"
                          onClick={() => {
                            const isTargeted = discoverProgress.jobMode === "targeted";
                            if (isTargeted) {
                              const areas = discoverProgress.targetAreas ?? [];
                              if (areas.length === 0) {
                                toast({ title: "Cannot resume — no target areas saved", variant: "destructive" });
                                return;
                              }
                              discoverVenuesTargeted.reset();
                              discoverVenuesTargeted.mutate({ areas, resume: true });
                            } else {
                              discoverVenues.reset();
                              discoverVenues.mutate({ resume: true });
                            }
                          }}
                          disabled={discoverJobRunning || discoverVenues.isPending || discoverVenuesTargeted.isPending}
                          className="bg-[#FED100] hover:bg-[#FED100]/90 text-zinc-950 font-semibold disabled:opacity-50"
                        >
                          Resume Job
                        </Button>
                        <Button
                          data-testid="button-start-fresh-discovery"
                          variant="outline"
                          onClick={() => {
                            const isTargeted = discoverProgress.jobMode === "targeted";
                            setDiscoverProgress((prev) => prev ? { ...prev, interrupted: false } : prev);
                            if (isTargeted) {
                              const areas = discoverProgress.targetAreas ?? [];
                              if (areas.length === 0) {
                                toast({ title: "No target areas saved — use the Targeted Sweep field instead.", variant: "destructive" });
                                return;
                              }
                              discoverVenuesTargeted.reset();
                              discoverVenuesTargeted.mutate({ areas, resume: false });
                            } else {
                              discoverVenues.reset();
                              discoverVenues.mutate({ resume: false });
                            }
                          }}
                          disabled={discoverJobRunning || discoverVenues.isPending || discoverVenuesTargeted.isPending}
                          className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                        >
                          Start Fresh
                        </Button>
                      </div>
                    </div>
                  )}
                  <div>
                    <Button
                      data-testid="button-run-discovery"
                      onClick={() => {
                        setDiscoverProgress(null);
                        discoverVenues.reset();
                        discoverVenues.mutate({ resume: false });
                      }}
                      disabled={discoverJobRunning || discoverVenues.isPending}
                      className="bg-[#FED100] hover:bg-[#FED100]/90 text-zinc-950 font-semibold disabled:opacity-50"
                    >
                      {discoverJobRunning || discoverVenues.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Searching…
                        </>
                      ) : (
                        "Run Discovery Job"
                      )}
                    </Button>
                  </div>

                  <div className="border-t border-zinc-800 pt-3 space-y-2" data-testid="section-targeted-sweep">
                    <div>
                      <p className="text-sm font-semibold text-zinc-200">Targeted Sweep</p>
                      <p className="text-xs text-zinc-500 mt-1">Search specific areas only — lower cost than a full run.</p>
                    </div>
                    <textarea
                      data-testid="textarea-targeted-areas"
                      rows={4}
                      value={targetedAreasInput}
                      onChange={(e) => setTargetedAreasInput(e.target.value)}
                      placeholder="e.g. Shoreditch, Soho, Camden&#10;or one area per line"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-lg p-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500 font-mono"
                    />
                    <Button
                      data-testid="button-run-targeted-sweep"
                      onClick={() => {
                        setDiscoverProgress(null);
                        discoverVenuesTargeted.reset();
                        discoverVenuesTargeted.mutate({ areas: parsedTargetedAreas, resume: false });
                      }}
                      disabled={
                        discoverJobRunning ||
                        discoverVenues.isPending ||
                        discoverVenuesTargeted.isPending ||
                        parsedTargetedAreas.length === 0
                      }
                      className="bg-[#FED100] hover:bg-[#FED100]/90 text-zinc-950 font-semibold disabled:opacity-50"
                    >
                      {discoverVenuesTargeted.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Starting…
                        </>
                      ) : (
                        `Run Targeted Sweep${parsedTargetedAreas.length > 0 ? ` (${parsedTargetedAreas.length})` : ""}`
                      )}
                    </Button>
                  </div>
                  {(discoverJobRunning || discoverVenues.isPending) && (
                    <div
                      className="flex items-center gap-2 text-sm text-zinc-300 bg-zinc-800/50 border border-zinc-700 rounded-lg p-3"
                      data-testid="status-discovery-loading"
                    >
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>
                        Searching London venues
                        {discoverProgress?.currentArea ? ` — ${discoverProgress.currentArea}` : ""}
                        {discoverProgress && discoverProgress.total > 0
                          ? ` (${discoverProgress.searched}/${discoverProgress.total})`
                          : ""}
                        …
                      </span>
                    </div>
                  )}
                  {!discoverJobRunning && !discoverVenues.isPending && discoverVenues.isError && (
                    <div
                      className="text-sm text-red-300 bg-red-950/40 border border-red-800 rounded-lg p-3"
                      data-testid="status-discovery-error"
                    >
                      {discoverVenues.error instanceof Error
                        ? discoverVenues.error.message
                        : "Failed to start discovery"}
                    </div>
                  )}
                  {!discoverJobRunning && !discoverVenues.isPending && !discoverVenues.isError && discoverProgress?.lastRun && (
                    <div
                      className="text-sm text-green-200 bg-green-950/40 border border-green-800 rounded-lg p-3"
                      data-testid="status-discovery-success"
                    >
                      <p className="font-medium text-green-100">Discovery complete</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                        <span>Searched: <span className="font-semibold" data-testid="text-discovery-searched">{discoverProgress.searched}</span></span>
                        <span>Discovered: <span className="font-semibold" data-testid="text-discovery-discovered">{discoverProgress.discovered}</span></span>
                        <span>Duplicates: <span className="font-semibold" data-testid="text-discovery-duplicates">{discoverProgress.duplicates}</span></span>
                        <span>Errors: <span className="font-semibold" data-testid="text-discovery-errors">{discoverProgress.errors}</span></span>
                      </div>
                    </div>
                  )}

                  <div className="pt-2" data-testid="panel-discovery-log">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Activity Log</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearDiscoveryLog}
                        disabled={discoverLog.length === 0 && !discoverProgress}
                        className="h-7 px-2 text-xs border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-300"
                        data-testid="button-clear-discovery-log"
                      >
                        Clear
                      </Button>
                    </div>
                    <div
                      ref={discoverLogRef}
                      className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 h-48 overflow-y-auto font-mono text-xs text-zinc-300 space-y-0.5"
                      data-testid="text-discovery-log"
                    >
                      {discoverLog.length === 0 ? (
                        <p className="text-zinc-600 italic">No activity yet. Run a discovery job to see live progress here.</p>
                      ) : (
                        discoverLog.map((line, i) => (
                          <div key={i} className="whitespace-pre-wrap break-words" data-testid={`log-line-${i}`}>{line}</div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              <div>
                <h2 className="text-2xl font-display font-bold">Google Review Photos</h2>
                <p className="text-zinc-500 text-sm mt-1">Pull venue photos from Google Reviews into the app</p>
              </div>

              <Card className="bg-zinc-900 border-zinc-800 p-5">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Status</h3>
                {googleStatus ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-xs text-zinc-500">Total Venues</p>
                      <p className="text-lg font-bold" data-testid="text-google-total">{googleStatus.totalVenues}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-xs text-zinc-500">Matched to Google</p>
                      <p className="text-lg font-bold text-blue-400" data-testid="text-google-matched">{googleStatus.matchedVenues}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-xs text-zinc-500">Venues with Photos</p>
                      <p className="text-lg font-bold text-green-400" data-testid="text-google-with-photos">{googleStatus.venuesWithPhotos}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-xs text-zinc-500">Total Photos Cached</p>
                      <p className="text-lg font-bold text-purple-400" data-testid="text-google-total-photos">{googleStatus.totalPhotos}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm">Loading status...</p>
                )}
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 p-5">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-2">Fill venue photos (one-off)</h3>
                <p className="text-xs text-zinc-500 mb-4">
                  Fetches SerpAPI photos for every venue that has a Google place ID but no photos yet, in small batches.
                  Run this on the <span className="text-zinc-300 font-medium">published (Production)</span> app to populate Production photos —
                  running it here only affects the development database. Safe to stop and re-run; it always picks up where it left off.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="button-photo-fill"
                    className="bg-violet-600 hover:bg-violet-700"
                    disabled={photoFillRunning}
                    onClick={runPhotoFill}
                  >
                    {photoFillRunning ? (
                      photoFillRetryIn !== null ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Retrying in {photoFillRetryIn}s…</>
                      ) : (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Filling photos…</>
                      )
                    ) : (
                      <><ImageIcon className="w-4 h-4 mr-2" /> Start photo fill</>
                    )}
                  </Button>
                  {photoFillRunning && (
                    <Button
                      data-testid="button-photo-fill-stop"
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      disabled={photoFillStopping}
                      onClick={stopPhotoFill}
                    >
                      {photoFillStopping ? "Stopping…" : "Stop"}
                    </Button>
                  )}
                </div>
                {photoFillProgress && (
                  <div className="mt-4 text-xs space-y-1" data-testid="status-photo-fill">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <span className="text-zinc-400">{photoFillProgress.batches} batches</span>
                      <span className="text-green-400">{photoFillProgress.withPhotos} venues got photos</span>
                      <span className="text-zinc-400">{photoFillProgress.processed} processed</span>
                      {photoFillProgress.errors > 0 && <span className="text-red-400">{photoFillProgress.errors} errors</span>}
                      {photoFillProgress.remaining !== null && (
                        <span className="text-zinc-400">{photoFillProgress.remaining} remaining</span>
                      )}
                    </div>
                    {photoFillRetryIn !== null && !photoFillProgress.done && (
                      <p className="text-yellow-400" data-testid="text-photo-fill-retry">
                        Paused on SerpAPI rate limit — retrying in {photoFillRetryIn}s. Press Stop to cancel.
                      </p>
                    )}
                    {photoFillProgress.done && (
                      <p className={photoFillProgress.rateLimited ? "text-yellow-400" : "text-zinc-500"}>
                        {photoFillProgress.rateLimited ? "Stopped on SerpAPI rate limit — re-run later to continue." : "Finished."}
                      </p>
                    )}
                  </div>
                )}
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 p-5">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Job History &amp; Errors</h3>
                <p className="text-xs text-zinc-500 mb-4">All scheduled data jobs run automatically. This view is read-only.</p>
                {googleStatus ? (
                  <div className="space-y-4">

                    <div className="border-b border-zinc-800 pb-3" data-testid="job-history-serpapi-histogram">
                      <p className="text-sm text-white font-medium">Fortnightly SerpAPI Histogram Refresh</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        Last Run: {googleStatus.serpHistogramJobStatus.lastRun
                          ? new Date(googleStatus.serpHistogramJobStatus.lastRun).toLocaleString()
                          : "Not run yet"}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-2">
                        <span className="text-zinc-400">Result: {googleStatus.serpHistogramJobStatus.lastResult}</span>
                        <span className="text-zinc-400">{googleStatus.serpHistogramJobStatus.processed} processed</span>
                        <span className="text-green-400">{googleStatus.serpHistogramJobStatus.withHistogram} with histogram</span>
                        <span className="text-yellow-400">{googleStatus.serpHistogramJobStatus.excludedCooldown} excluded by cooldown</span>
                        {googleStatus.serpHistogramJobStatus.errors > 0 && (
                          <span className="text-red-400">{googleStatus.serpHistogramJobStatus.errors} errors</span>
                        )}
                      </div>
                      {googleStatus.serpHistogramJobStatus.lastSummary && (
                        <p className="text-xs text-zinc-500 mt-2 break-words">
                          {googleStatus.serpHistogramJobStatus.lastSummary}
                        </p>
                      )}
                    </div>

                    <div className="border-b border-zinc-800 pb-3" data-testid="job-history-claude-estimates">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <p className="text-sm text-white font-medium">Weekly Claude Venue Estimates</p>
                        <span className={googleStatus.claudeEstimateJobStatus.enabled ? "text-xs text-green-400" : "text-xs text-zinc-500"}>
                          {googleStatus.claudeEstimateJobStatus.enabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        Last Run: {googleStatus.claudeEstimateJobStatus.lastRun
                          ? new Date(googleStatus.claudeEstimateJobStatus.lastRun).toLocaleString()
                          : "Not run yet"}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-2">
                        <span className="text-zinc-400">Result: {googleStatus.claudeEstimateJobStatus.lastResult}</span>
                        {googleStatus.claudeEstimateJobStatus.lastSummary && (
                          <span className="text-zinc-400">Summary: {googleStatus.claudeEstimateJobStatus.lastSummary}</span>
                        )}
                      </div>
                      {googleStatus.claudeEstimateJobStatus.lastLogs?.length ? (
                        <div className="mt-2 text-xs text-zinc-500 space-y-1">
                          <p className="text-zinc-400">Recent errors / logs:</p>
                          {googleStatus.claudeEstimateJobStatus.lastLogs.slice(-5).map((log, index) => (
                            <p key={`${log}-${index}`} className="font-mono break-words">{log}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-zinc-500 mt-2">Recent errors / logs: None</p>
                      )}
                    </div>

                    {discoverProgress?.lastRun && (
                      <div className="border-b border-zinc-800 pb-3" data-testid="job-history-discover">
                        <p className="text-sm text-white font-medium">Discover London Venues</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Last Run: {new Date(discoverProgress.lastRun).toLocaleString()}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-2">
                          <span className="text-green-400">{discoverProgress.discovered} discovered</span>
                          <span className="text-yellow-400">{discoverProgress.duplicates} duplicates</span>
                          {discoverProgress.errors > 0 && <span className="text-red-400">{discoverProgress.errors} errors</span>}
                        </div>
                      </div>
                    )}

                    {googleStatus.matchJobStatus?.lastRun && (
                      <div className="border-b border-zinc-800 pb-3" data-testid="job-history-match">
                        <p className="text-sm text-white font-medium">Match Venues</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Last Run: {new Date(googleStatus.matchJobStatus.lastRun).toLocaleString()}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-2">
                          <span className="text-zinc-400">{googleStatus.matchJobStatus.total} total</span>
                          <span className="text-green-400">{googleStatus.matchJobStatus.matched} matched</span>
                          <span className="text-yellow-400">{googleStatus.matchJobStatus.noMatch} not found</span>
                          {googleStatus.matchJobStatus.errors > 0 && (
                            <span className="text-red-400">{googleStatus.matchJobStatus.errors} errors</span>
                          )}
                        </div>
                      </div>
                    )}

                    {googleStatus.updateDataJobStatus?.lastRun && (
                      <div className="border-b border-zinc-800 pb-3" data-testid="job-history-update-data">
                        <p className="text-sm text-white font-medium">Update Ratings, Prices &amp; Hours</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Last Sync: {new Date(googleStatus.updateDataJobStatus.lastRun).toLocaleString()}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-2">
                          <span className="text-zinc-400">{googleStatus.updateDataJobStatus.total} total</span>
                          <span className="text-primary">{googleStatus.updateDataJobStatus.updated} updated</span>
                          {googleStatus.updateDataJobStatus.errors > 0 && (
                            <span className="text-red-400">{googleStatus.updateDataJobStatus.errors} errors</span>
                          )}
                        </div>
                      </div>
                    )}

                    {!discoverProgress?.lastRun
                      && !googleStatus.serpHistogramJobStatus?.lastRun
                      && !googleStatus.matchJobStatus?.lastRun
                      && !googleStatus.updateDataJobStatus?.lastRun
                      && (
                        <p className="text-zinc-500 text-sm">No jobs have run yet. Status will appear here once the scheduler triggers them.</p>
                      )}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm">Loading job history...</p>
                )}
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 p-5">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Add a missing venue</h3>
                <p className="text-sm text-white mb-3">Look up a single venue by name &amp; coordinates</p>
                <div className="flex gap-2 items-start">
                  <input
                    data-testid="input-lookup-name"
                    type="text"
                    placeholder="e.g. The Lord Stanley"
                    value={lookupName}
                    onChange={e => setLookupName(e.target.value)}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500"
                  />
                  <input
                    data-testid="input-lookup-lat"
                    type="number"
                    placeholder="e.g. 51.5472"
                    step={0.0001}
                    value={lookupLat}
                    onChange={e => setLookupLat(e.target.value)}
                    className="w-36 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500"
                  />
                  <input
                    data-testid="input-lookup-lng"
                    type="number"
                    placeholder="e.g. -0.1296"
                    step={0.0001}
                    value={lookupLng}
                    onChange={e => setLookupLng(e.target.value)}
                    className="w-36 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500"
                  />
                  <Button
                    data-testid="button-lookup-venue"
                    className="bg-violet-600 hover:bg-violet-700 shrink-0"
                    disabled={lookupLoading || !lookupName || !lookupLat || !lookupLng}
                    onClick={async () => {
                      setLookupLoading(true);
                      try {
                        const res = await fetch("/api/admin/venues/lookup", {
                          method: "POST",
                          credentials: "include",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ name: lookupName, lat: Number(lookupLat), lng: Number(lookupLng) }),
                        });
                        const data = await res.json();
                        toast({ title: data.message });
                        if (res.status === 201) {
                          setLookupName("");
                          setLookupLat("");
                          setLookupLng("");
                        }
                      } catch {
                        toast({ title: "Request failed", variant: "destructive" });
                      } finally {
                        setLookupLoading(false);
                      }
                    }}
                  >
                    {lookupLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Look up and add"}
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">Use Google Maps to find coordinates — right click any location on Google Maps and click the coordinates to copy them.</p>
              </Card>


              <Card className="bg-zinc-900 border-zinc-800 p-5">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">How It Works</h3>
                <div className="space-y-2 text-sm text-zinc-400">
                  <p>All Google jobs are scheduled — no manual triggers.</p>
                  <p>• <strong className="text-white">Match Venues</strong> — links each venue to its Google Place ID using name and coordinates.</p>
                  <p>• <strong className="text-white">Photos</strong> — gallery photos come from SerpAPI as permanent CDN links, backfilled automatically on the fortnightly schedule for every venue with a Google Place ID.</p>
                  <p>• <strong className="text-white">Update Ratings, Prices &amp; Hours</strong> — pulls rating, review count, price level, and opening hours for matched venues.</p>
                  <p>• <strong className="text-white">Display</strong> — photos appear in each venue's detail view as a scrollable gallery; $ meter shows price level.</p>
                  <p className="pt-2 text-xs text-zinc-500">Use the form above to add a single missing venue manually if it didn't get picked up by Discover.</p>
                </div>
              </Card>
            </div>
          ) : activePanel === "ra-events" ? (
            <div className="max-w-2xl space-y-6">
              <div>
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-6 h-6 text-primary" />
                  <h2 className="text-2xl font-display font-bold">Resident Advisor Events</h2>
                </div>
                <p className="text-zinc-500 text-sm mt-1">Ingest scraped RA event data into the venues database</p>
              </div>

              <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Upload Apify scrape output (.json)
                  </label>
                  <label
                    htmlFor="ra-file-input"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-dashed border-zinc-700 bg-background text-zinc-400 text-sm cursor-pointer hover:border-zinc-600 hover:text-zinc-300 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    <span data-testid="text-ra-file-label">Choose a .json file</span>
                  </label>
                  <input
                    id="ra-file-input"
                    data-testid="input-ra-file"
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={(e) => handleRaFileSelected(e.target.files?.[0] ?? null)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Or paste JSON directly
                  </label>
                  <textarea
                    data-testid="textarea-ra-json"
                    value={raJsonText}
                    onChange={(e) => { setRaJsonText(e.target.value); setRaInlineError(null); setRaServerError(null); setRaResult(null); }}
                    rows={8}
                    placeholder='[{"title":"...","venueName":"...","startTime":"..."}]'
                    className="w-full bg-background border border-zinc-700 rounded-md px-3 py-2 text-sm text-white font-mono placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  />
                </div>

                {raInlineError && (
                  <p data-testid="text-ra-inline-error" className="text-sm text-red-400">{raInlineError}</p>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    data-testid="button-ra-ingest"
                    onClick={handleRaIngestSubmit}
                    disabled={raSubmitting || raClearing}
                    className="bg-primary hover:bg-primary text-primary-foreground font-semibold disabled:opacity-50"
                  >
                    {raSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Ingesting…
                      </>
                    ) : (
                      "Ingest Events"
                    )}
                  </Button>

                  <Button
                    data-testid="button-ra-clear"
                    onClick={handleRaClear}
                    disabled={raSubmitting || raClearing}
                    className="bg-background border border-red-500 text-red-500 hover:bg-red-500/10 font-semibold disabled:opacity-50"
                  >
                    {raClearing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Clearing…
                      </>
                    ) : (
                      "Clear RA Events"
                    )}
                  </Button>
                </div>

                {!raSubmitting && !raClearing && !raServerError && raClearResult && (
                  <div
                    data-testid="status-ra-clear-result"
                    className="rounded-md border border-zinc-800 bg-background p-4 text-sm"
                  >
                    <p className="text-green-400">✓ Cleared {raClearResult.deleted} RA events</p>
                    <p className="text-xs text-zinc-500 pt-1">
                      Run at {formatRaTimestamp(raClearResult.at)}
                    </p>
                  </div>
                )}

                {raSubmitting && (
                  <div data-testid="status-ra-loading" className="flex items-center gap-2 text-zinc-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Ingesting events…
                  </div>
                )}

                {!raSubmitting && raServerError && (
                  <div data-testid="status-ra-error" className="text-sm text-red-400">
                    {raServerError}
                  </div>
                )}

                {!raSubmitting && !raServerError && raResult && (
                  <div
                    data-testid="status-ra-result"
                    className="rounded-md border border-zinc-800 bg-background p-4 space-y-1 text-sm"
                  >
                    <p>
                      <span className="text-green-400">✅ {raResult.inserted}</span>
                      <span className="text-zinc-400"> inserted</span>
                    </p>
                    <p>
                      <span className="text-zinc-300">⏭️ {raResult.skipped}</span>
                      <span className="text-zinc-400"> skipped (duplicates)</span>
                    </p>
                    <p>
                      <span className="text-red-400">❌ {raResult.errors}</span>
                      <span className="text-zinc-400"> errors</span>
                    </p>
                    <p className="text-xs text-zinc-500 pt-1">
                      Run at {formatRaTimestamp(raResult.at)}
                    </p>
                  </div>
                )}
              </Card>
            </div>
          ) : activePanel === "applications" ? (
            <ApplicationsPanel />
          ) : (
            <div className="h-full flex items-center justify-center text-zinc-600">
              <div className="text-center">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">Select a venue to manage</p>
                <p className="text-sm mt-1">Toggle sponsorship, assign business users, or remove venues</p>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

interface BusinessApplicationRow {
  id: number;
  userId: number;
  businessName: string;
  venueId: number;
  applicantRole: string;
  businessEmail: string | null;
  companiesHouseNumber: string | null;
  status: string;
  createdAt: string | null;
  applicant: { id: number; username: string; email: string | null } | null;
  venue: { id: number; name: string; type: string } | null;
}

function ApplicationsPanel() {
  const { toast } = useToast();
  const { data: apps = [], isLoading } = useQuery<BusinessApplicationRow[]>({
    queryKey: ["/api/admin/business-applications", { status: "pending" }],
    queryFn: async () => {
      const res = await fetch("/api/admin/business-applications?status=pending", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load applications");
      return res.json();
    },
  });

  const approve = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/business-applications/${id}/approve`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).message || "Approve failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/business-applications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/business-users"] });
      toast({ title: "Application approved", description: "User promoted to business and assigned to venue." });
    },
    onError: (e: any) => toast({ title: "Approval failed", description: e.message, variant: "destructive" }),
  });

  const reject = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/business-applications/${id}/reject`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).message || "Reject failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/business-applications"] });
      toast({ title: "Application rejected" });
    },
    onError: (e: any) => toast({ title: "Reject failed", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-amber-400" />
          <h2 className="text-2xl font-display font-bold">Business Applications</h2>
        </div>
        <p className="text-zinc-500 text-sm mt-1">Review pending requests from users who signed up as a business.</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-zinc-500" data-testid="text-applications-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : apps.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-8 text-center" data-testid="text-applications-empty">
          <Briefcase className="w-10 h-10 mx-auto mb-3 text-zinc-600 opacity-50" />
          <p className="text-zinc-400 text-sm">No pending applications.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {apps.map(app => (
            <Card
              key={app.id}
              className="bg-zinc-900 border-zinc-800 p-4"
              data-testid={`card-application-${app.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="space-y-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white" data-testid={`text-application-venue-${app.id}`}>
                      {app.venue?.name ?? app.businessName}
                    </span>
                    {app.venue?.type && (
                      <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px] capitalize">
                        {app.venue.type}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400" data-testid={`text-application-applicant-${app.id}`}>
                    <span className="text-zinc-500">Applicant:</span>{" "}
                    {app.applicant?.username ?? `User #${app.userId}`}
                    {app.applicant?.email && <span className="text-zinc-600"> · {app.applicant.email}</span>}
                  </p>
                  <p className="text-sm text-zinc-400">
                    <span className="text-zinc-500">Role:</span> {app.applicantRole}
                  </p>
                  {app.businessEmail && (
                    <p className="text-xs text-zinc-500">Business email: {app.businessEmail}</p>
                  )}
                  {app.companiesHouseNumber && (
                    <p className="text-xs text-zinc-500">Companies House: {app.companiesHouseNumber}</p>
                  )}
                  {app.createdAt && (
                    <p className="text-xs text-zinc-600">Submitted {new Date(app.createdAt).toLocaleString()}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    data-testid={`button-reject-application-${app.id}`}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-zinc-300 hover:bg-red-950 hover:text-red-300 hover:border-red-900"
                    disabled={reject.isPending || approve.isPending}
                    onClick={() => reject.mutate(app.id)}
                  >
                    <ThumbsDown className="w-4 h-4 mr-1.5" />
                    Reject
                  </Button>
                  <Button
                    data-testid={`button-approve-application-${app.id}`}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    disabled={approve.isPending || reject.isPending}
                    onClick={() => approve.mutate(app.id)}
                  >
                    <ThumbsUp className="w-4 h-4 mr-1.5" />
                    Approve
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
